import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Download, Receipt, User, Calendar, MessageSquare, CheckCircle, Loader2, Hash } from 'lucide-react';
import { formatCurrency, formatCurrencyFull, formatDate, formatMonth } from '../lib/utils';
import api from '../lib/api';
import LabelProfileModal from './LabelProfileModal';

const PaymentDetailsModal = ({ invoice, open, onOpenChange }) => {
    const [downloading, setDownloading] = useState(false);
    const [labelProfileOpen, setLabelProfileOpen] = useState(false);

    if (!invoice) return null;

    const rejectedStatuses = ['rejected', 'rejected_finance', 'rejected_founder', 'founder_rejected', 'finance_rejected'];
    const isRejected = rejectedStatuses.includes(invoice.status);
    const rejectionReason = invoice.founder_remarks || invoice.finance_remarks || invoice.finance_notes;

    const downloadReceipt = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/invoices/${invoice.id}/bank-receipt/download`);
            const { filename, data } = res.data;
            // data is base64; build a data URL and trigger download
            const byteChars = atob(data);
            const byteNums = new Array(byteChars.length).fill(0).map((_, i) => byteChars.charCodeAt(i));
            const blob = new Blob([new Uint8Array(byteNums)]);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || `${invoice.invoice_number}-receipt`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to download receipt');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-testid="payment-details-modal">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                            <DialogTitle className="font-heading">Payment Details</DialogTitle>
                            <DialogDescription className="font-mono text-xs">
                                {invoice.invoice_number}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-4 pt-2">
                    {/* Amount */}
                    <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Paid Amount</div>
                        <div className="text-3xl font-bold text-green-500 font-mono">
                            {formatCurrencyFull(invoice.amount, invoice.currency || 'INR')}
                        </div>
                        <div className="mt-1 text-sm text-muted-foreground">
                            to{' '}
                            <button
                                type="button"
                                onClick={() => setLabelProfileOpen(true)}
                                className="font-medium text-foreground hover:text-primary hover:underline cursor-pointer"
                                title="View label profile"
                                data-testid="payment-modal-label-link"
                            >
                                {invoice.label_name}
                            </button>
                            {invoice.month && <> for <span className="font-medium text-foreground">{formatMonth(invoice.month)}</span></>}
                        </div>
                    </div>

                    {/* Meta grid */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg border border-border/40">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1">
                                <Calendar className="w-3 h-3" /> Paid on
                            </div>
                            <div className="text-sm font-medium" data-testid="paid-on-value">
                                {invoice.paid_on
                                    ? formatDate(invoice.paid_on)
                                    : invoice.paid_at ? formatDate(invoice.paid_at) : '—'}
                            </div>
                        </div>
                        <div className="p-3 rounded-lg border border-border/40">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1">
                                <User className="w-3 h-3" /> Paid by
                            </div>
                            <div className="text-sm font-medium" data-testid="paid-by-value">
                                {invoice.paid_by_name || '—'}
                            </div>
                        </div>
                        <div className="p-3 rounded-lg border border-border/40 col-span-2">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1">
                                <Hash className="w-3 h-3" /> UTR / Reference Number
                            </div>
                            <div className="text-sm font-medium font-mono" data-testid="utr-value">
                                {invoice.utr_number || <span className="text-muted-foreground font-sans">—</span>}
                            </div>
                        </div>
                    </div>

                    {/* Label-facing rejection reason only. Internal Finance/Founder approval notes are not shown here. */}
                    {isRejected && rejectionReason && (
                        <div className="p-3 rounded-lg border border-destructive/30 bg-destructive/5">
                            <div className="text-[10px] uppercase tracking-wider text-destructive flex items-center gap-1 mb-1">
                                <MessageSquare className="w-3 h-3" /> Rejection Reason
                            </div>
                            <div className="text-sm whitespace-pre-wrap" data-testid="rejection-reason">
                                {rejectionReason}
                            </div>
                        </div>
                    )}

                    {/* Payment note is public only after payment. */}
                    {invoice.status === 'paid' && invoice.payment_comment && (
                        <div className="p-3 rounded-lg border border-border/40">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1">
                                <MessageSquare className="w-3 h-3" /> Payment Note
                            </div>
                            <div className="text-sm whitespace-pre-wrap" data-testid="payment-remarks">
                                {invoice.payment_comment}
                            </div>
                        </div>
                    )}

                    {/* Receipt */}
                    <div className="p-3 rounded-lg border border-border/40 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <Receipt className="w-4 h-4 text-muted-foreground" />
                            <div className="text-sm">
                                {invoice.bank_receipt_filename ? (
                                    <>
                                        <div className="font-medium">{invoice.bank_receipt_filename}</div>
                                        {invoice.bank_receipt_uploaded_at && (
                                            <div className="text-xs text-muted-foreground">
                                                uploaded {formatDate(invoice.bank_receipt_uploaded_at)}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span className="text-muted-foreground">No bank receipt uploaded</span>
                                )}
                            </div>
                        </div>
                        {invoice.bank_receipt_filename && (
                            <Button
                                size="sm"
                                onClick={downloadReceipt}
                                disabled={downloading}
                                data-testid="download-receipt-btn"
                                className="bg-primary hover:bg-primary/90"
                            >
                                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                <span className="ml-2">Download</span>
                            </Button>
                        )}
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                        <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30">PAID</Badge>
                        {invoice.approved_at && (
                            <span className="text-xs text-muted-foreground">
                                approved {formatDate(invoice.approved_at)}
                            </span>
                        )}
                    </div>
                </div>
            </DialogContent>
            {/* Nested label-profile modal — read-only KYC viewer */}
            <LabelProfileModal
                open={labelProfileOpen}
                onOpenChange={setLabelProfileOpen}
                labelId={invoice.label_id}
            />
        </Dialog>
    );
};

export default PaymentDetailsModal;
